import React, { useEffect, useState } from "react";
import { citationHref, isWebUrl } from "@/lib/citationMarkers";
import { useToast } from "@/hooks/useToast";
import { Sheet, SheetTrigger } from "../ui/sheet";
import {
  ChevronDown,
  Copy,
  ScrollText,
  Check,
} from "lucide-react";
import { ReferencePanel } from "../ReferencePanel";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import {
  citationEntryContent,
  citationEntryKey,
  orderCitationsByAppearance,
  resolveCitationOpenTarget,
  type CitationEntry,
} from "./citationUtils";

interface MessageCitationsProps {
  message: {
    id: string;
    content?: string;
    meta_data?: {
      citation: CitationEntry[];
    };
  };
  /** Same normalized content BotMessage used for display numbers (avoids order desync). */
  citationContent?: string;
  /**
   * citation_key => URL of the cited source document (ENG-1845). When set, the
   * References row title links there instead of the municipality-website mapping.
   */
  sourceHrefByKey?: Map<string, string>;
}

const CITATION_ROW_CLASS =
  "citation-message-text mb-2 grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-2";

interface CitationRowProps {
  citationNumber: number;
  badgeClasses: string;
  onBadgeClick: () => void;
  titleText: string;
  titleHref?: string;
  actions: React.ReactNode;
}

const CitationRow = ({
  citationNumber,
  badgeClasses,
  onBadgeClick,
  titleText,
  titleHref,
  actions,
}: CitationRowProps) => (
  <article className={CITATION_ROW_CLASS}>
    <button
      type="button"
      className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border text-[11px] font-semibold ${badgeClasses}`}
      onClick={onBadgeClick}
      aria-label={`Open reference ${citationNumber}`}
    >
      {citationNumber}
    </button>
    <div className="citation-message-value min-w-0">
      {titleHref ? (
        <a
          href={titleHref}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {titleText}
        </a>
      ) : (
        titleText
      )}
    </div>
    <div className="citation-actions-wrapper flex shrink-0 self-stretch items-center gap-4 border-l border-gray-300 pl-3">
      {actions}
    </div>
  </article>
);

const MessageCitations = (props: MessageCitationsProps) => {
  const { message, citationContent, sourceHrefByKey } = props;
  const contentForOrdering = citationContent ?? message.content ?? "";

  const [expandedMessageCitation, setExpandedMessageCitation] = useState<string | null>(null);
  const [inlineFlyoutOpen, setInlineFlyoutOpen] = useState(false);
  const [inlineFlyoutTitle, setInlineFlyoutTitle] = useState("");
  const [inlineFlyoutContent, setInlineFlyoutContent] = useState("");

  const toggleExpansionCitation = (id: string) => {
    if (expandedMessageCitation === id) {
      setExpandedMessageCitation(null);
    } else {
      setExpandedMessageCitation(id);
    }
  };

  const [openPanels, setOpenPanels] = useState<Record<string, boolean>>({});

  const setPanelOpen = (citationKey: string, open: boolean) => {
    setOpenPanels((prev) => ({
      ...prev,
      [citationKey]: open,
    }));
  };

  const { toast } = useToast();

  const orderedCitations = orderCitationsByAppearance(
    message.meta_data?.citation,
    contentForOrdering,
  );
  const totalCitations = orderedCitations.length;
  const citedCount = orderedCitations.filter((item) => item.cited).length;
  const consultedCount = Math.max(totalCitations - citedCount, 0);

  const openCitation = (entry: CitationEntry) => {
    const key = citationEntryKey(entry);
    const content = citationEntryContent(entry);
    setInlineFlyoutTitle(formatCitationDisplay(key));
    setInlineFlyoutContent(content);
    setInlineFlyoutOpen(true);
  };

  // citation:open detail is { messageId, key, contentForOrdering?, originalIndex? }.
  // Prefer originalIndex when present so Answer Reasoning can open the exact duplicate-key chunk.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent;
      const detail = customEvent.detail || {};
      if (String(detail.messageId) !== String(message.id)) return;

      const rawKey = typeof detail.key === "string" ? detail.key : "";
      const originalIndex =
        typeof detail.originalIndex === "number" ? detail.originalIndex : undefined;
      const resolveContent =
        typeof detail.contentForOrdering === "string"
          ? detail.contentForOrdering
          : contentForOrdering;
      const match = resolveCitationOpenTarget(
        message.meta_data?.citation,
        resolveContent,
        rawKey,
        originalIndex,
      );
      if (!match) return;
      openCitation(match.entry);
    };

    window.addEventListener("citation:open", handler);
    return () => {
      window.removeEventListener("citation:open", handler);
    };
  }, [message.id, contentForOrdering, message.meta_data?.citation]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        toast({
          title: "Reference copied!",
          description: "The reference has been copied to your clipboard.",
          variant: "success",
        });
      },
      (err) => {
        console.error("Failed to copy text: ", err);
      },
    );
  };

  const formatCitationDisplay = (citationKey: string) => {
    if (isWebUrl(citationKey)) {
      return citationKey.trim();
    }

    const websitePattern = /^(.+?)\s*-\s*(\/\S+)/;
    const match = citationKey.match(websitePattern);

    if (match) {
      const [, siteName, path] = match;

      // ponytail: hardcoded municipality base URLs; follow-up ticket to move to backend config.
      const siteUrlMap: Record<string, string> = {
        "Sallisaw Website": "https://sallisawok.org",
        "Sallisaw's Government Website": "https://sallisawok.org",
        "Sallisaw Government Website": "https://sallisawok.org",
        "La Quinta Website": "https://www.laquintaca.gov",
        "Lowell Website": "https://www.lowellarkansas.gov",
        "Decatur Website": "https://www.cityofdecatur.in.gov",
        "Pea Ridge Website": "https://cityofpearidge.com",
        "Charlestown Website": "https://www.charlestownmd.org",
        "Johnson Website": "https://www.cityofjohnson.com",
      };

      const baseUrl = siteUrlMap[siteName.trim()];

      if (baseUrl) {
        return `${baseUrl}${path.trim()}`;
      }
    }

    return citationKey;
  };

  const getCopyText = (citationKey: string) => {
    const formattedText = formatCitationDisplay(citationKey);
    return formattedText !== citationKey ? formattedText : citationKey;
  };

  return (
    <>
      <Sheet open={inlineFlyoutOpen} onOpenChange={setInlineFlyoutOpen}>
        <ReferencePanel
          reference={{}}
          title={inlineFlyoutTitle}
          content={inlineFlyoutContent}
          onCopy={() => copyToClipboard(inlineFlyoutContent)}
        />
      </Sheet>
      {totalCitations > 0 ? (
        <aside className="additional-info-dropdown mb-8">
          <div
            role="button"
            className="additional-info-dropdown-title"
            style={{ cursor: "pointer" }}
            onClick={() => toggleExpansionCitation(message.id)}
            onKeyDown={(e) => (e.key === "Enter" ? toggleExpansionCitation(message.id) : null)}
            tabIndex={0}
          >
            <div className="flex flex-row items-center">
              <p className="dropdown-secondary mr-2 font-semibold">References</p>
              <ChevronDown
                className={`secondary-dropdown-arrow max-h-4 content-center transform transition-transform duration-200 ${
                  expandedMessageCitation === message.id ? "rotate-180" : "rotate-0"
                }`}
              />
            </div>
          </div>
          {expandedMessageCitation === message.id && (
            <div className="p2 additional-info-content citation-list-wrapper pt-4 mt-2 border-t border-gray-300">
              {orderedCitations.map(({ entry: citation, key, cited }, index) => {
                const citationNumber = index + 1;
                const badgeClasses = cited
                  ? "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800/50 dark:bg-blue-900/30 dark:text-blue-300"
                  : "border-gray-300 bg-gray-100 text-gray-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300";
                const hasBody = typeof citation !== "string";
                const displayText = formatCitationDisplay(key);
                const copyText = getCopyText(key);
                const sourceHref = sourceHrefByKey?.get(key);
                const shouldLinkCitation = displayText !== key || isWebUrl(key);
                const body = citationEntryContent(citation);
                const panelKey = `${message.id}-${index}-${key}`;

                return (
                  <CitationRow
                    key={`message-${message.id}-citation-${key}-${index}`}
                    citationNumber={citationNumber}
                    badgeClasses={badgeClasses}
                    onBadgeClick={() => openCitation(citation)}
                    titleText={displayText}
                    titleHref={
                      sourceHref ?? (shouldLinkCitation ? citationHref(displayText) : undefined)
                    }
                    actions={
                      hasBody ? (
                        <>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={() => copyToClipboard(copyText)}
                                  onKeyDown={(e) =>
                                    e.key === "Enter" ? copyToClipboard(copyText) : null
                                  }
                                  tabIndex={0}
                                >
                                  <Copy className="h-4 w-4" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Copy reference</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <Sheet
                            open={openPanels[panelKey]}
                            onOpenChange={(open) => setPanelOpen(panelKey, open)}
                          >
                            <SheetTrigger asChild>
                              <button
                                type="button"
                                className="hidden sm:inline-flex items-center gap-1 opacity-75 hover:opacity-100 focus:opacity-100"
                              >
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <ScrollText className="h-5 w-5" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>View reference</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex items-center justify-center rounded-full bg-green-500 h-4 w-4">
                                        <Check className="h-3 w-3 text-white" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Verified</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <span className="sr-only">Toggle reference panel</span>
                              </button>
                            </SheetTrigger>
                            <ReferencePanel
                              reference={citation}
                              title={displayText}
                              content={body}
                              onCopy={() => copyToClipboard(body)}
                            />
                          </Sheet>
                        </>
                      ) : (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="citation-message-copy-wrapper"
                                onClick={() => copyToClipboard(copyText)}
                                onKeyDown={(e) =>
                                  e.key === "Enter" ? copyToClipboard(copyText) : null
                                }
                                tabIndex={0}
                              >
                                <Copy className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Copy reference</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )
                    }
                  />
                );
              })}
            </div>
          )}
          {expandedMessageCitation === message.id && totalCitations > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              This response cited {citedCount} {citedCount === 1 ? "reference" : "references"} and consulted {consultedCount} {consultedCount === 1 ? "additional source" : "additional sources"}.
              All sources inform the response; citations appear where a source directly supports a statement.
            </p>
          )}
        </aside>
      ) : (
        <div className="mb-8" />
      )}
    </>
  );
};

export default MessageCitations;
